@if (invoice(); as inv) {
<!-- Scrollable Body Container (Entire Aside Scrolls Naturally) -->
<div class="flex-1 overflow-y-auto overflow-x-hidden min-h-0">
  <!-- Section 1: Invoice Metadata -->
  <div class="p-4 space-y-3.5 border-b border-border">
    <!-- Status & Quick Actions (Stacked to eliminate horizontal overflow) -->
    <div class="space-y-2.5">
      <div class="flex items-center justify-between gap-2 min-w-0">
        <span class="text-lg font-bold text-foreground truncate">{{ inv.invoiceNumber }}</span>
        <app-badge
          [title]="formatStatusLabel(inv.status)"
          [variant]="getStatusVariant(inv.status)"
          size="xs"
        />
      </div>
      <div class="flex items-center flex-wrap gap-1.5">
        @if (inv.status !== 'voided' && inv.status !== 'VOIDED' && canEditBilling()) {
          <app-button
            variant="outline"
            size="xs"
            icon="send"
            (click)="openResendModal()"
          >
            Resend
          </app-button>
        }
        <app-button
          variant="outline"
          size="xs"
          icon="file-spreadsheet"
          (click)="openUploadScheduleModal()"
        >
          Upload Schedule
        </app-button>
        <app-button
          variant="outline"
          size="xs"
          icon="paperclip"
          (click)="openUploadDocumentModal('other')"
        >
          Upload Doc
        </app-button>
        @if (inv.status !== 'voided' && inv.status !== 'VOIDED' && canEditBilling()) {
          <app-button
            variant="outline"
            size="xs"
            icon="ban"
            (click)="openVoidModal()"
          >
            Void
          </app-button>
        }
        @if (canEditBilling()) {
          <app-button
            variant="outline"
            size="xs"
            [icon]="inv.isArchived ? 'rotate-ccw' : 'archive'"
            [loading]="isArchiving()"
            (click)="toggleArchiveInvoice()"
          >
            {{ inv.isArchived ? 'Restore' : 'Archive' }}
          </app-button>
        }
      </div>
    </div>

  <!-- Metadata Grid -->
  <div class="grid grid-cols-2 gap-3">
    <div>
      <span class="text-xs text-muted uppercase tracking-wider">Billed To</span>
      <p class="text-sm font-medium text-foreground truncate">{{ inv.billedToName || '—' }}</p>
    </div>
    <div>
      <span class="text-xs text-muted uppercase tracking-wider">Method</span>
      <div class="mt-0.5">
        <app-badge
          [title]="inv.invoicingMethod === 'factoring' ? 'Factoring' : 'Direct'"
          [variant]="getMethodVariant(inv.invoicingMethod)"
          size="xs"
        />
      </div>
    </div>
    <div>
      <span class="text-xs text-muted uppercase tracking-wider">Created</span>
      <p class="text-sm text-foreground">{{ formatDate(inv.createdAt) }}</p>
    </div>
    <div>
      <span class="text-xs text-muted uppercase tracking-wider">Invoiced</span>
      <p class="text-sm text-foreground">{{ formatDate(inv.invoicedAt) }}</p>
    </div>
  </div>

  <!-- Financial Summary -->
  <div class="bg-surface rounded-lg border border-border p-3 space-y-2">
    <div class="flex items-center justify-between">
      <span class="text-xs font-semibold text-muted uppercase tracking-wider">Financial Summary</span>
      @if (canEditBilling() && inv.status !== 'voided' && inv.status !== 'VOIDED' && !isEditingFinancials()) {
        <app-button
          variant="ghost"
          size="xs"
          icon="pencil"
          (click)="startEditingFinancials()"
        >
          Edit Amounts
        </app-button>
      }
    </div>

    <div class="flex justify-between text-sm">
      <span class="text-muted">Total Gross Amount</span>
      <span class="font-medium text-foreground">{{ inv.totalAmount | currency }}</span>
    </div>

    @if (!isEditingFinancials()) {
      @if (inv.feeAmount) {
        <div class="flex justify-between text-sm">
          <span class="text-muted">Fees</span>
          <span class="font-medium text-danger">-{{ inv.feeAmount | currency }}</span>
        </div>
      }
      <div class="flex justify-between text-sm pt-2 mt-2 border-t border-border/50">
        <span class="text-muted font-medium">Net Amount</span>
        <span class="font-semibold text-foreground">{{ (inv.totalAmount - (inv.feeAmount || 0)) | currency }}</span>
      </div>
      <div class="flex justify-between text-sm">
        <span class="text-muted">Received</span>
        <span class="font-medium text-primary">{{ inv.receivedAmount | currency }}</span>
      </div>
      <hr class="border-border" />
      <div class="flex justify-between text-sm">
        <span class="text-muted font-medium">Balance Due</span>
        <span
          class="font-bold"
          [class.text-primary]="balanceDue() === 0"
          [class.text-warning]="balanceDue() > 0"
          [class.text-danger]="balanceDue() < 0"
        >
          {{ balanceDue() | currency }}
        </span>
      </div>

      <!-- Factoring Fee Breakdown -->
      @if (inv.invoicingMethod === 'factoring' && inv.factoringFees) {
        <hr class="border-border" />
        <div class="space-y-1">
          <span class="text-xs text-muted uppercase tracking-wider">Factoring Fees</span>
          <div class="flex justify-between text-xs">
            <span class="text-muted">Base Fee ({{ inv.factoringFees.basePercentage }}%)</span>
            <span class="text-foreground">{{ inv.factoringFees.baseFee | currency }}</span>
          </div>
          <div class="flex justify-between text-xs">
            <span class="text-muted">Reserve ({{ inv.factoringFees.reservePercentage }}%)</span>
            <span class="text-foreground">{{ inv.factoringFees.reserveAmount | currency }}</span>
          </div>
          <div class="flex justify-between text-xs">
            <span class="text-muted">Transaction Fee</span>
            <span class="text-foreground">{{ inv.factoringFees.transactionFee | currency }}</span>
          </div>
        </div>
      }
    } @else {
      <!-- Inline Edit Mode for Fees & Received Amount -->
      <div
        animate.enter="hv-banner-enter"
        animate.leave="hv-banner-leave"
        class="space-y-2.5 pt-2 border-t border-border"
      >
        @if (inv.invoicingMethod === 'factoring') {
          <div class="grid grid-cols-3 gap-2">
            <div>
              <span class="text-[11px] text-muted block mb-1">Base Fee</span>
              <app-number-input
                currency="USD"
                size="sm"
                [min]="0"
                [ngModel]="editBaseFee()"
                (ngModelChange)="editBaseFee.set($event || 0)"
              />
            </div>
            <div>
              <span class="text-[11px] text-muted block mb-1">Reserve</span>
              <app-number-input
                currency="USD"
                size="sm"
                [min]="0"
                [ngModel]="editReserveAmount()"
                (ngModelChange)="editReserveAmount.set($event || 0)"
              />
            </div>
            <div>
              <span class="text-[11px] text-muted block mb-1">Wire / Tx Fee</span>
              <app-number-input
                currency="USD"
                size="sm"
                [min]="0"
                [ngModel]="editTransactionFee()"
                (ngModelChange)="editTransactionFee.set($event || 0)"
              />
            </div>
          </div>
        } @else {
          <div>
            <span class="text-[11px] text-muted block mb-1">Fees / Deductions</span>
            <app-number-input
              currency="USD"
              size="sm"
              [min]="0"
              [ngModel]="editDirectFeeAmount()"
              (ngModelChange)="editDirectFeeAmount.set($event || 0)"
            />
          </div>
        }

        <div class="flex justify-between text-xs text-muted pt-1">
          <span>Total Fees: <strong class="text-danger">-{{ editTotalFees() | currency }}</strong></span>
          <span>Net Amount: <strong class="text-foreground">{{ editNetAmount() | currency }}</strong></span>
        </div>

        <div>
          <div class="flex items-center justify-between mb-1">
            <span class="text-[11px] text-muted font-medium">Received (Funded) Amount</span>
            @if (editReceivedAmount() !== editNetAmount()) {
              <app-button
                variant="outline"
                size="xs"
                (click)="matchReceivedToNet()"
              >
                Match Net ({{ editNetAmount() | currency }})
              </app-button>
            }
          </div>
          <app-number-input
            currency="USD"
            size="sm"
            [min]="0"
            [ngModel]="editReceivedAmount()"
            (ngModelChange)="editReceivedAmount.set($event || 0)"
          />
        </div>

        <div class="flex justify-between text-xs pt-1 border-t border-border">
          <span class="text-muted font-medium">New Balance Due</span>
          <span
            class="font-bold"
            [class.text-primary]="editBalanceDue() === 0"
            [class.text-warning]="editBalanceDue() > 0"
            [class.text-danger]="editBalanceDue() < 0"
          >
            {{ editBalanceDue() | currency }}
          </span>
        </div>

        <div class="flex items-center justify-end gap-2 pt-1">
          <app-button
            variant="outline"
            size="xs"
            [disabled]="isSavingFinancials()"
            (click)="cancelEditingFinancials()"
          >
            Cancel
          </app-button>
          <app-button
            variant="primary"
            size="xs"
            icon="check"
            [loading]="isSavingFinancials()"
            (click)="saveInvoiceFinancials()"
          >
            Save Amounts
          </app-button>
        </div>
      </div>
    }
  </div>

  <!-- Load Count & Created By -->
  <div class="flex items-center justify-between text-xs text-muted">
    <div class="flex items-center gap-1">
      <lucide-icon name="package" class="size-3.5"></lucide-icon>
      <span>{{ inv.loadIds?.length || 0 }} loads</span>
    </div>
    @if (inv.createdByEmail) {
      <span class="truncate max-w-[180px]">{{ inv.createdByEmail }}</span>
    }
  </div>
</div>

<!-- Sub-section Navigation Toggle (Loads vs Documents) -->
<div class="px-4 py-2.5 border-b border-border bg-surface sticky top-0 z-10 flex items-center justify-between gap-2">
  <app-button-group
    [options]="[
      { value: 'loads', label: 'Loads (' + loadSnapshots().length + ')' },
      { value: 'documents', label: 'Documents (' + documents().length + ')' }
    ]"
    [ngModel]="activeSection()"
    (ngModelChange)="activeSection.set($event)"
    size="sm"
  ></app-button-group>

  @if (activeSection() === 'documents') {
    <app-button
      variant="outline"
      size="xs"
      icon="plus"
      (click)="openUploadDocumentModal()"
    >
      Upload
    </app-button>
  }
</div>

@if (activeSection() === 'loads') {
<!-- Section 2: Loads List (flows naturally in outer scroll container) -->
<div animate.enter="hv-fade-up-sm" class="flex flex-col">
  <!-- Loads Header -->
  <div class="px-4 py-3 border-b border-border space-y-2">
    <div class="flex items-center justify-between">
      <app-heading level="h4" class="text-sm font-semibold tracking-wide text-muted uppercase">
        Loads ({{ loadSnapshots().length }})
      </app-heading>
    </div>
    <!-- Search Input -->
    <app-input
      placeholder="Search loads..."
      prefixIcon="search"
      size="sm"
      [value]="loadsSearchTerm()"
      (valueChange)="loadsSearchTerm.set($event || '')"
    ></app-input>
  </div>

  <!-- Loads List -->
  <div>
    @if (isLoadingLoads()) {
      <div class="flex items-center justify-center py-12">
        <app-loading variant="compact" title="Loading loads..." />
      </div>
    } @else if (filteredLoads().length === 0) {
      <div class="flex flex-col items-center justify-center py-12 text-center px-4">
        <lucide-icon name="search-x" class="size-8 text-muted mb-2"></lucide-icon>
        <app-text variant="muted" size="sm">No loads found</app-text>
      </div>
    } @else {
      <div class="divide-y divide-border">
        @for (snap of filteredLoads(); track snap.loadId) {
          <div class="p-3 space-y-2 hover:bg-surface/50 transition-colors">
            <!-- Load Row: ID, Counterparty, Amount & Edit Pricing -->
            <div class="flex items-start justify-between gap-2">
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 flex-wrap">
                  <span class="text-sm font-semibold text-foreground font-mono">
                    {{ getLoadDisplayId(snap) }}
                  </span>
                  @if (snap.rawLoadRef && snap.rawLoadRef !== snap.externalId) {
                    <app-badge
                      [title]="'Ref: ' + snap.rawLoadRef"
                      variant="info"
                      size="xs"
                    />
                  }
                  <app-badge
                    [title]="formatStatusLabel(getLoadPaymentStatus(snap))"
                    [variant]="getPaymentStatusVariant(getLoadPaymentStatus(snap))"
                    size="xs"
                  />
                  @if (snap.isSplitSchedulePayment) {
                    <app-badge
                      title="Split Schedule"
                      variant="secondary"
                      size="xs"
                    />
                  }
                  @if (snap.pricing?.isTonu) {
                    <app-badge
                      title="TONU"
                      variant="warning"
                      size="xs"
                    />
                  }
                </div>
                <p class="text-xs text-muted truncate mt-0.5">
                  {{ getLoadCounterparty(snap) }}
                </p>
              </div>
              <div class="flex items-center gap-2 shrink-0">
                <span class="text-sm font-bold text-foreground whitespace-nowrap">
                  {{ getLoadTotal(snap) | currency }}
                </span>
                @if (canEditBilling() && inv.status !== 'voided' && inv.status !== 'VOIDED') {
                  <app-button
                    variant="ghost"
                    size="xs"
                    icon="pencil"
                    title="Edit Pricing"
                    (click)="openEditLoadPricing(snap)"
                  ></app-button>
                  <app-button
                    variant="ghost"
                    size="xs"
                    icon="trash-2"
                    title="Remove from Invoice"
                    class="text-danger hover:text-danger"
                    (click)="openRemoveLoad(snap)"
                  ></app-button>
                }
              </div>
            </div>

            <!-- Per-load Payment Controls -->
            @if (canEditLoadPayments()) {
              <div class="mt-3 pt-3 border-t border-border/50">
                <div class="flex items-end gap-3">
                  <!-- Status Dropdown -->
                  <div class="flex-1 min-w-0">
                    <span class="text-[11px] font-medium text-muted uppercase tracking-wider mb-1.5 block">Payment Status</span>
                    <app-select
                      [options]="loadPaymentStatusOptions"
                      size="sm"
                      placeholder="Status"
                      [ngModel]="getLoadPaymentStatus(snap)"
                      (ngModelChange)="setPendingStatus(snap, $event)"
                    />
                  </div>

                  <!-- Paid Amount Input (conditional) -->
                  @if (showAmountInput(snap)) {
                    <div
                      animate.enter="hv-banner-enter"
                      animate.leave="hv-banner-leave"
                      class="w-32 shrink-0"
                    >
                      <span class="text-[11px] font-medium text-muted uppercase tracking-wider mb-1.5 block">Amount Paid</span>
                      <app-number-input
                        [currency]="'USD'"
                        size="sm"
                        [min]="0"
                        [max]="getLoadTotal(snap)"
                        placeholder="0.00"
                        [ngModel]="getLoadPaidAmount(snap)"
                        (ngModelChange)="setPendingAmount(snap, $event)"
                      />
                    </div>
                  }
                </div>
              </div>
            }
          </div>
        }
      </div>
    }
  </div>

  <!-- Global Save Action Bar -->
  @if (pendingChanges().size > 0) {
    <div
      animate.enter="hv-float-bar-enter"
      animate.leave="hv-float-bar-leave"
      class="sticky bottom-0 p-4 border-t border-border bg-surface-elevated shrink-0 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-10 flex items-center justify-between"
    >
      <span class="text-sm text-muted font-medium">
        {{ pendingChanges().size }} pending update{{ pendingChanges().size === 1 ? '' : 's' }}
      </span>
      <div class="flex gap-2">
        <app-button
          variant="outline"
          size="sm"
          (click)="discardPendingChanges()"
          [disabled]="isSavingAll()"
        >
          Discard
        </app-button>
        <app-button
          variant="primary"
          size="sm"
          icon="save"
          [loading]="isSavingAll()"
          (click)="saveAllPendingChanges()"
        >
          Save Changes
        </app-button>
      </div>
    </div>
  }
</div>
} @else {
<!-- Section 3: Documents List -->
<div animate.enter="hv-fade-up-sm" class="p-4 space-y-4">
  <!-- Payout Schedule / Remittance Callout / Card (Supports M:N schedules for both Factoring & Direct) -->
  @if (factoringScheduleDocs().length > 0) {
    <div class="space-y-2.5">
      <div class="flex items-center justify-between">
        <span class="text-xs font-semibold uppercase tracking-wider text-muted">
          Payout Schedules ({{ factoringScheduleDocs().length }})
        </span>
        <app-button
          variant="outline"
          size="xs"
          icon="plus"
          (click)="openUploadScheduleModal()"
        >
          Add Schedule
        </app-button>
      </div>
      @for (schedDoc of factoringScheduleDocs(); track schedDoc.id) {
        <div class="p-3.5 rounded-xl border border-primary/30 bg-primary-low-contrast space-y-2.5">
          <div class="flex items-start justify-between gap-2">
            <div class="flex items-center gap-2.5 min-w-0">
              <div class="w-8 h-8 rounded-lg bg-surface border border-border flex items-center justify-center text-primary shrink-0 shadow-xs">
                <lucide-icon name="file-spreadsheet" size="18"></lucide-icon>
              </div>
              <div class="min-w-0">
                <span class="text-xs font-semibold text-foreground block truncate">{{ schedDoc.title || 'Payout Schedule' }}</span>
                <span class="text-[11px] text-muted block truncate">{{ schedDoc.fileName }}</span>
              </div>
            </div>
            <app-badge
              [title]="inv.invoicingMethod === 'factoring' ? 'Factoring Schedule' : 'Remittance Advice'"
              variant="primary"
              size="xs"
            ></app-badge>
          </div>

          <div class="flex items-center justify-between pt-2 border-t border-primary/20 text-xs">
            <span class="text-muted text-[11px]">
              Uploaded {{ formatDate(schedDoc.uploadedAt) }}
            </span>
            <div class="flex items-center gap-1.5">
              <app-button
                variant="outline"
                size="xs"
                icon="eye"
                (click)="openDocument(schedDoc)"
              >
                View
              </app-button>
              <app-button
                variant="ghost"
                size="xs"
                [iconOnly]="true"
                icon="external-link"
                title="Open in New Tab"
                (click)="openDocumentExternal(schedDoc)"
              ></app-button>
              <app-button
                variant="ghost"
                size="xs"
                [iconOnly]="true"
                icon="trash-2"
                (click)="deleteDocument(schedDoc)"
              ></app-button>
            </div>
          </div>
        </div>
      }
    </div>
  } @else {
    <!-- Call to action: Upload Payout Schedule / Remittance Advice -->
    <div class="p-4 rounded-xl border border-dashed border-border bg-surface text-center space-y-2">
      <div class="w-10 h-10 rounded-full bg-surface-low border border-border mx-auto flex items-center justify-center text-muted">
        <lucide-icon name="file-spreadsheet" size="20"></lucide-icon>
      </div>
      <div>
        <app-text weight="semibold" size="sm" class="block">
          {{ inv.invoicingMethod === 'factoring' ? 'No Factoring Schedule' : 'No Remittance Schedule' }}
        </app-text>
        <app-text variant="muted" size="xs" class="block mt-0.5">
          {{ inv.invoicingMethod === 'factoring'
            ? 'Upload the settlement schedule PDF to AI-extract load payouts and fees.'
            : 'Upload the direct remittance advice PDF to AI-extract load payouts.' }}
        </app-text>
      </div>
      <app-button
        variant="outline"
        size="xs"
        icon="upload"
        (click)="openUploadScheduleModal()"
      >
        Upload Schedule
      </app-button>
    </div>
  }

  <!-- Documents List -->
  <div class="space-y-2">
    <div class="flex items-center justify-between">
      <app-heading level="h4" class="text-xs font-semibold tracking-wide text-muted uppercase">
        Attached Documents ({{ documents().length }})
      </app-heading>
    </div>

    @if (documents().length === 0) {
      <div class="py-8 text-center px-4 bg-surface rounded-xl border border-border">
        <lucide-icon name="file-text" class="size-8 text-muted mx-auto mb-2"></lucide-icon>
        <app-text variant="muted" size="sm" class="block">No documents attached yet</app-text>
        <p class="text-xs text-muted mt-1">Upload schedules, remittance advice, NOAs, or payment receipts.</p>
        <div class="mt-3">
          <app-button
            variant="outline"
            size="sm"
            icon="plus"
            (click)="openUploadDocumentModal()"
          >
            Upload Document
          </app-button>
        </div>
      </div>
    } @else {
      <div class="divide-y divide-border rounded-xl border border-border bg-surface overflow-hidden">
        @for (doc of documents(); track doc.id) {
          <div class="p-3 flex items-center justify-between gap-3 hover:bg-surface-low transition-colors">
            <div class="flex items-center gap-3 min-w-0">
              <div class="w-9 h-9 rounded-lg bg-surface-low border border-border flex items-center justify-center shrink-0 text-primary">
                @if (doc.type === 'factoring_schedule') {
                  <lucide-icon name="file-spreadsheet" size="18"></lucide-icon>
                } @else {
                  <lucide-icon name="file-text" size="18"></lucide-icon>
                }
              </div>
              <div class="min-w-0">
                <div class="flex items-center gap-2 flex-wrap">
                  <span class="text-sm font-medium text-foreground truncate block">
                    {{ doc.title || doc.fileName }}
                  </span>
                  <app-badge
                    [title]="getDocumentTypeLabel(doc.type)"
                    [variant]="doc.type === 'factoring_schedule' ? 'primary' : 'secondary'"
                    size="xs"
                  ></app-badge>
                </div>
                <span class="text-xs text-muted block mt-0.5">
                  {{ doc.fileName }} &middot; {{ formatDate(doc.uploadedAt) }}
                  @if (doc.uploadedByName) {
                    &middot; {{ doc.uploadedByName }}
                  }
                </span>
              </div>
            </div>

            <div class="flex items-center gap-1 shrink-0">
              <app-button
                variant="ghost"
                size="sm"
                [iconOnly]="true"
                icon="eye"
                (click)="openDocument(doc)"
                title="View in App"
              ></app-button>
              <app-button
                variant="ghost"
                size="sm"
                [iconOnly]="true"
                icon="external-link"
                (click)="openDocumentExternal(doc)"
                title="Open in New Tab"
              ></app-button>
              <app-button
                variant="ghost"
                size="sm"
                [iconOnly]="true"
                icon="trash-2"
                (click)="deleteDocument(doc)"
                title="Delete Document"
              ></app-button>
            </div>
          </div>
        }
      </div>
    }
  </div>
</div>
}
</div> <!-- End of flex-1 overflow-y-auto min-h-0 -->

<!-- Sticky Footer Quick Actions (Upload Schedule & Record Payment - Unified for Factoring & Direct, Zero Horizontal Overflow) -->
<div class="p-3 md:p-4 border-t border-border bg-surface shrink-0 w-full overflow-hidden">
  <div class="grid grid-cols-2 gap-2.5 w-full">
    <app-button
      variant="outline"
      size="sm"
      icon="file-spreadsheet"
      class="w-full block"
      (click)="openUploadScheduleModal()"
    >
      {{ factoringScheduleDocs().length > 0 ? 'Add Schedule' : 'Upload Schedule' }}
    </app-button>

    @if (canRecordPayment() && balanceDue() > 0) {
      <app-button
        variant="primary"
        size="sm"
        icon="credit-card"
        class="w-full block"
        (click)="openRecordPaymentModal()"
      >
        Record Payment
      </app-button>
    } @else {
      <div class="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-surface-high border border-border text-xs font-semibold text-success w-full">
        <lucide-icon name="check-circle" size="14"></lucide-icon>
        Paid in Full
      </div>
    }
  </div>
</div>
} @else {
<div class="flex flex-col items-center justify-center h-full px-4 text-center">
  <lucide-icon name="file-text" class="size-10 text-muted mb-3"></lucide-icon>
  <app-text variant="muted" size="sm">No invoice selected</app-text>
</div>
}
